import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Alert,
  Link,
  CircularProgress
} from "@mui/material";
import { IoEye, IoEyeOff } from "react-icons/io5";
import { School as SchoolIcon } from '@mui/icons-material';
import config from "./config";
import { setToken } from "./api/authFetch";
import { adminColors, TOUCH_HIT_SX } from "./theme/adminTheme";
import { FONT_STACK, BORDER_COLOR } from "./theme/uiConstants";
import { BrandHeader } from "./pages/legal/LegalPageLayout";
import { isStaffRole } from "./constants/roles";

// Shared focus treatment for form fields — indigo ring/label, no default MUI asterisk.
const fieldFocusSx = {
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: adminColors.accent,
    borderWidth: "1.5px",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: adminColors.accent,
  },
  "& .MuiFormLabel-asterisk": {
    display: "none",
  },
};

export default function Signin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [mounted, setMounted] = useState(false);

  // Set when login is blocked due to no access (403) — adds a note above
  // the "Request access" link and carries the typed username through as a
  // prefilled email on /request-access.
  const [noAccess, setNoAccess] = useState(false);

  const navigate = useNavigate();

  const userRef = useRef(null);

  useEffect(() => {
    userRef.current?.focus();
    setMounted(true);
  }, []);

  const handleSignin = async (e) => {
    e.preventDefault();
    setErrMsg("");
    setSubmitting(true);

    try {
      const signinUrl = `${config.API_BASE_URL}/api/signin`;

      const res = await fetch(signinUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ username, password }),
      });

      if (res.ok) {
        const data = await res.json();
        // Primary cookie set by the server may be dropped by the browser
        // (incognito / third-party-cookie blocking / cross-site Safari
        // ITP), so also store the token ourselves and send it as a Bearer
        // header on every request (see api/authFetch.js).
        setToken(data.access_token);

        // Navigate based on role
        if (isStaffRole(data.role)) {
          navigate("/admin");
        } else {
          navigate("/chat");
        }
      } else {
        const errorData = await res.json();
        setErrMsg(errorData.detail || "Login failed");

        // A 403 means the account exists but is blocked from access
        // (revoked access or outside the allowed time slot). Surface a
        // note pointing at the "Request access" link below instead of
        // just showing the error text.
        if (res.status === 403) {
          setNoAccess(true);
        }
      }
    } catch (error) {
      console.error("Login failed:", error?.message ?? error);
      setErrMsg("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!mounted) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: adminColors.surface
      }}>
        <CircularProgress sx={{ color: adminColors.accent }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: adminColors.surface,
        display: "flex",
        flexDirection: "column",
        fontFamily: FONT_STACK,
        "@media (prefers-reduced-motion: reduce)": {
          "*": {
            transitionDuration: "0.01ms !important",
            animationDuration: "0.01ms !important",
          },
        },
      }}
    >
      {/* Navigation header — shared with the forgot/reset-password pages
          and the legal shell (BrandHeader), rather than a hand-rolled copy.
          BrandHeader's logo is a real <button>, keyboard-operable, unlike
          the plain onClick-only Link this used to be. */}
      <BrandHeader />

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2
        }}
      >
      <Card
        sx={{
          width: "100%",
          maxWidth: 420,
          p: 4,
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          border: `1px solid ${BORDER_COLOR}`,
          borderRadius: "12px"
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              backgroundColor: adminColors.accent,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 3
            }}
          >
            <SchoolIcon sx={{ color: "white", fontSize: 24 }} />
          </Box>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: adminColors.ink,
              mb: 1,
              fontSize: { xs: '1.5rem', sm: '2rem' },
              lineHeight: 1.2
            }}
          >
            Welcome{" "}
            <Box component="span" sx={{ color: adminColors.accent }}>
              Students and Educators
            </Box>
          </Typography>
        </Box>

        {/* Error Message */}
        {errMsg && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              "& .MuiAlert-message": {
                fontSize: "14px"
              }
            }}
          >
            {errMsg}
          </Alert>
        )}

        {/* Form */}
        <Box component="form" onSubmit={handleSignin}>
          <TextField
            fullWidth
            label="Username"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            ref={userRef}
            required
            sx={{ mb: 2.5, ...fieldFocusSx }}
            disabled={submitting}
          />

          <TextField
            fullWidth
            label="Password"
            type={showPw ? "text" : "password"}
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            sx={{ mb: 3, ...fieldFocusSx }}
            disabled={submitting}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPw(!showPw)}
                    edge="end"
                    size="small"
                    aria-label={showPw ? "Hide password" : "Show password"}
                    sx={{ color: adminColors.inkSoft }}
                  >
                    {showPw ? <IoEyeOff size={20} /> : <IoEye size={20} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
            <Link
              component="button"
              type="button"
              onClick={() => navigate("/forgot-password")}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.85rem",
                fontWeight: 600,
                color: adminColors.inkSoft,
                cursor: "pointer",
                "&:hover": { color: adminColors.accent },
                ...TOUCH_HIT_SX,
              }}
            >
              Forgot password?
            </Link>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={submitting || !username || !password}
            sx={{
              mb: 3.5,
              py: 1.5,
              backgroundColor: adminColors.accent,
              "&:hover": {
                backgroundColor: adminColors.accentDark
              },
              "&.Mui-disabled": {
                backgroundColor: adminColors.accentBorder,
                color: adminColors.accentDark
              },
              textTransform: "none",
              fontWeight: 600,
              fontSize: 15,
              borderRadius: "8px",
              transition: "transform 120ms ease-out, background-color 150ms ease",
              "&:active": {
                transform: submitting ? "none" : "scale(0.98)"
              }
            }}
          >
            {submitting ? (
              <CircularProgress size={20} sx={{ color: adminColors.accentDark }} />
            ) : (
              "Sign in"
            )}
          </Button>

        </Box>

        {/* Request access — secondary action pointing at its own page.
            When login was blocked due to no access (403), a short note
            surfaces above the link and `blocked: true` carries through so
            /request-access shows the "no access yet" framing regardless of
            whether the typed username happens to be an email. The email
            prefill is a separate, best-effort convenience — it only fires
            when the username looks like an email. */}
        <Box sx={{ mt: 3, textAlign: "center" }}>
          {noAccess && (
            <Typography variant="body2" sx={{ color: adminColors.inkSoft, mb: 1 }}>
              Your account doesn't have access yet.
            </Typography>
          )}
          <Typography variant="body2" sx={{ color: adminColors.inkSoft }}>
            Don't have access?{" "}
            <Link
              component="button"
              type="button"
              onClick={() =>
                navigate("/request-access", {
                  state: {
                    email: username.includes("@") ? username : "",
                    blocked: noAccess,
                  },
                })
              }
              sx={{
                fontWeight: 600,
                color: adminColors.accent,
                cursor: "pointer",
                verticalAlign: "baseline",
                "&:hover": { color: adminColors.accentDark }
              }}
            >
              Request access
            </Link>
          </Typography>
        </Box>

        {/* Legal links — small, out of the way of the primary sign-in task,
            but present on every logged-out entry point. TOUCH_HIT_SX pads
            the tap target to 44px on touch input without inflating the
            visible text on desktop. */}
        <Box sx={{ mt: 3, display: "flex", justifyContent: "center", gap: 2 }}>
          <Link
            component="button"
            type="button"
            onClick={() => navigate("/privacy")}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.8rem",
              color: adminColors.inkSoft,
              cursor: "pointer",
              "&:hover": { color: adminColors.accent },
              ...TOUCH_HIT_SX,
            }}
          >
            Privacy Policy
          </Link>
          <Link
            component="button"
            type="button"
            onClick={() => navigate("/terms")}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.8rem",
              color: adminColors.inkSoft,
              cursor: "pointer",
              "&:hover": { color: adminColors.accent },
              ...TOUCH_HIT_SX,
            }}
          >
            Terms of Service
          </Link>
        </Box>
      </Card>
      </Box>
    </Box>
  );
}
